import { digger } from '@cbre/digger';
import { DataObject } from '@shared/models/data-object.model';

/**
 * Returns very first object from digger search
 * NOTE: this fn only is used by `makeTemplatedRequest` only, is not adding any validation and
 * is expecting that each path returns leaves (not nodes)
 * @param data any
 * @param path string
 */
export const getValueOrFirst = (data, path) => {
  const value = digger(data, path);
  return Array.isArray(value) ? value[0] : value;
};

/**
 * Walks trhough recursively a template object and populates a new one using template's values(paths)
 * WARNNG for data with arrays paths, it expects only to have one child e.g. to generate a request
 * of specific usage in a property, instead of sending the full record, a new object with subset of usages
 * needs to be passed
 * `{ ...property, usages: [targetUsage] }`
 * so you get
 * `{
 *   resource_id: 1,
 *   property_usages.vacant_f: true
 *   //...
 * }`
 *
 * @param data source data
 * @param template target object to populate with passed tokens
 */
export const makeTemplatedRequest = (data: any, template: any) => {
  if (typeof template === 'string') {
    return getValueOrFirst(data, template);
  }
  return Object.entries(template).reduce((acc, [key, path]) => {
    if (typeof path === 'string') {
      return { ...acc, [key]: getValueOrFirst(data, path) };
    } else if (Array.isArray(path)) {
      return { ...acc, [key]: path.map(childPath => makeTemplatedRequest(data, childPath)) };
    } else if (typeof path === 'object') {
      return { ...acc, [key]: makeTemplatedRequest(data, path) };
    }
    return acc;
  }, {});
};

// Helper function to check if floor or suite number has changed
const checkFloorSuiteChange = (data: any, originalRecords: any[]): boolean => {
  if (!data.floor_suite || !Array.isArray(data.floor_suite) || !originalRecords?.length) {
    return false;
  }

  const newFloorSuite = data.floor_suite[0];
  const newFloorId = newFloorSuite?.floor_suite?.[0]?.id;
  const originalRecord = originalRecords.find(record => record?.floor_suite?.[0]?.id === newFloorId);

  // Check if floor_number or suite_number has changed
  const newFloorNumber = newFloorSuite?.floor_number;
  const newSuiteNumber = newFloorSuite?.suite_number;
  const originalFloorNumber = originalRecord?.floor_suite?.[0]?.floor_number;
  const originalSuiteNumber = originalRecord?.floor_suite?.[0]?.suite_number;

  return (
    (newFloorNumber !== undefined && newFloorNumber !== originalFloorNumber) ||
    (newSuiteNumber !== undefined && newSuiteNumber !== originalSuiteNumber)
  );
};

export const makeLeaseSpaceTemplatedRequest = (data: any, records: any[], fieldNames: string[]) => {
  const floorSuiteKeys = ['floor_number', 'suite_number'];
  const mandatoryFields = [
    'entity.id',
    'property.id',
    'property.property_usage.id',
    'floor_suite/id',
    'floor_suite/floor_number',
    'floor_suite/suite_number',
  ];

  // Field mapping for specific keys
  const fieldKeyMapping = {
    'entity.id': 'lease_id',
    'property.id': 'property_id',
    'property.property_usage.id': 'property_usage_id',
  };

  // Special mapping for floor_suite paths
  const floorSuiteKeyMapping = {
    id: 'id',
  };

  const result: any = {};

  // Combine fieldName with mandatoryFields and remove duplicates
  const allFields = [...new Set([...fieldNames, ...mandatoryFields])];

  // Process all fields in a single loop
  allFields.forEach(field => {
    if (field.includes('/')) {
      const [key, path] = field.split('/');

      // Initialize array structure if needed
      if (!result[key]) {
        result[key] = [{}];
      }

      // Use mapped key for floor_suite paths if it exists, otherwise use the original path
      const resultPath = key === 'floor_suite' && floorSuiteKeyMapping[path] ? floorSuiteKeyMapping[path] : path;
      result[key][0][resultPath] = getValueOrFirst(data, field);
    } else {
      // Use mapped key if it exists, otherwise use the field name
      const resultKey = fieldKeyMapping[field] || field;
      result[resultKey] = getValueOrFirst(data, field);
    }
  });

  // Check if floor suite has changed and add to result
  const hasFloorSuiteKey = floorSuiteKeys.some(key => fieldNames.some(field => field.includes(key)));
  result.hasFloorSuiteChanged = hasFloorSuiteKey ? checkFloorSuiteChange(data, records) : false;

  return result;
};

/**
 * WIP
 * Walks trhough object and get the right part of object and returns as array (removes dups)
 * `{ source_id: 'entity.id', }, tim_id: 'entity.id',}` => `['entity.id']`
 * @param data
 */

const _getFieldsTokens = data => {
  return (
    Object.entries(data)
      .reduce((acc: any[], [, value]) => {
        if (typeof value === 'string') {
          return [...acc, `${value}`];
        } else if (Array.isArray(value)) {
          return [...acc, ...value.map(_getFieldsTokens).reduce((acc2, x) => [...acc2, ...x], [])];
        } else if (typeof value === 'object') {
          return [...acc, ..._getFieldsTokens(value)];
        }
        return acc;
      }, [])
      // remove dups
      .filter((x, i, self) => self.indexOf(x) === i)
  );
};

/**
 * Splits column.field by
 * @param col
 * @returns
 */
export const getFlatPaths = (field: string): string[] => {
  return field
    .split('/')
    .map(subName => subName.split('.'))
    .reduce((acc, p) => [...acc, ...p], []);
};
/**
 * Return sub-group type of a field(substring of full field/col.field)
 * @param field
 * @param path
 * @returns
 */
export const getPathType = (field: string, path: string) => {
  const idxOfArrays = field.indexOf(`${path}/`);
  const idxOfNested = field.indexOf(`${path}.`);
  return idxOfArrays > -1 && (idxOfArrays < idxOfNested || idxOfNested === -1) ? 'array' : 'group';
};

/**
 * It creates/updates target object entries from field and assigns entry to the leaf part of field notation
 */
export const makeTemplateData = (target: DataObject, field: string, entry: any): DataObject => {
  const paths = getFlatPaths(field);
  let [nextPath] = paths;
  let lastPath = nextPath;
  let idx = 0;
  let nextSpot = target;

  while (nextPath != null && idx < paths.length - 1) {
    const pathType = getPathType(field, nextPath);
    if (pathType === 'array') {
      // array
      if (nextSpot[nextPath] != null) {
        const [firstElement] = nextSpot[nextPath];
        if (firstElement == null) {
          nextSpot[nextPath].push({});
          [nextSpot] = nextSpot[nextPath];
        } else {
          nextSpot = firstElement;
        }
      } else {
        nextSpot[nextPath] = [{}];
        [nextSpot] = nextSpot[nextPath];
      }
    } else {
      // group
      if (nextSpot[nextPath] != null) {
        nextSpot = nextSpot[nextPath];
      } else {
        nextSpot[nextPath] = {};
        nextSpot = nextSpot[nextPath];
      }
    }
    idx++;
    nextPath = paths[idx];
  }
  lastPath = paths[idx];
  if (lastPath === '') {
    nextSpot = Object.assign(nextSpot, entry);
  } else {
    nextSpot[lastPath] = entry;
  }
  return target;
};

// tslint:disable:max-line-length
/**
 * Replaces select special characters, provided by map, in query param with new strings
 * @param query Query string to be edited
 * @param specialCharacterEncodingMap Map of special characters with their respective replacement strings (HTML encoding characters)
 * @returns Updated new query string
 */
export const standardizeHtmlQueryParam = (
  query: string,
  specialCharacterEncodingMap: { [key: string]: string }
): string => {
  let newQuery = query;
  Object.keys(specialCharacterEncodingMap).forEach(key => {
    newQuery = newQuery.replace(key, specialCharacterEncodingMap[key]);
  });
  return newQuery;
};
